<script>
import Vue from 'vue';
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import VueClipboard from 'vue-clipboard2';

import { UnraidLoading } from '@unraid/shared-components';
import UiDropdown from '../components/Ui/Dropdown.vue';
import SuccessTile from '../components/Registration/Success/Tile.vue';
// import HelperCountdownToFire from '../components/Helper/CountdownToFire.vue';

import { CAN_POST_MESSAGE, ERROR_MESSAGE_UNEXPECTED_OWNER_RESPONSE } from '../consts';
import { log } from '../log';

import i18n from '../i18n';
import store from '../store';

Vue.use(VueClipboard);

export default {
  name: 'EventStatus',
  i18n,
  store,
  metaInfo: {
    title: i18n.tc('regWizPopUp.routes.success.heading.default'),
  },
  components: {
    UnraidLoading,
    UiDropdown,
    SuccessTile,
    // HelperCountdownToFire,
  },
  data: () => ({
    CAN_POST_MESSAGE,
    loading: true,
    timeout: false,
    closePopUp: false,
    backToHome: false,
    copyFeedback: null,
    keyUrlCopied: false,
  }),
  computed: {
    ...mapState([
      'accountAction',
      'keyServerAction',
      'licenseAction',
      'submissionFrom',
      'servername',
      'username',
      'license',
      'site',
      'state',
      'receivedSuccessNewServerState',
      'errorMessage',
      'showPostMessageStatus',
    ]),
    ...mapGetters(['stateData']),
    licenseType() {
      if (this.license.includes('Trial.key')) return this.$t('regWizPopUp.routes.success.keys.trial');
      if (this.license.includes('Basic.key')) return this.$t('regWizPopUp.routes.success.keys.basic');
      if (this.license.includes('Plus.key')) return this.$t('regWizPopUp.routes.success.keys.plus');
      if (this.license.includes('Pro.key')) return this.$t('regWizPopUp.routes.success.keys.pro');
      return '';
    },
    performingActions() {
      if (this.timeout) return false;
      if (this.license && this.licenseAction !== null) return !(this.licenseAction);
      if (this.submissionFrom === 'SignIn' || this.submissionFrom === 'SignOut') return !(this.accountAction);
      return true;
    },
    heading() {
      return this.username ? this.$t('regWizPopUp.routes.success.heading.username', [this.username]) : this.$t('regWizPopUp.routes.success.heading.default');
    },
    subheading() {
      if (this.submissionFrom === 'ExtendTrial') return this.$t('regWizPopUp.routes.success.subheading.extention');
      if (this.submissionFrom === 'SignIn' && this.keyServerAction === 'new') return this.$t('regWizPopUp.routes.success.subheading.newTrial');
      return null;
    },
    signInTileTitle() {
      if (!this.accountAction) return this.$t('regWizPopUp.routes.success.signIn.tileTitle.loading', [this.servername]);
      if (!this.accountAction.success) return this.$t('regWizPopUp.routes.success.signIn.tileTitle.actionFail', [this.servername]);
      return this.$t('regWizPopUp.routes.success.signIn.tileTitle.actionSuccess', [this.servername]);
    },
    signOutTileTitle() {
      if (!this.accountAction) return this.$t('regWizPopUp.routes.success.signOut.tileTitle.loading', [this.servername]);
      if (!this.accountAction.success) return this.$t('regWizPopUp.routes.success.signOut.tileTitle.actionFail', [this.servername]);
      return this.$t('regWizPopUp.routes.success.signOut.tileTitle.actionSuccess', [this.servername]);
    },
    helperAutoFireText() {
      if (this.keyInstallError && !this.keyUrlCopied) return this.$t('regWizPopUp.routes.success.install.copyBeforeClose');
      return this.backToHome
        ? this.$t('regWizPopUp.routes.success.countdown.error.text')
        : this.$t('regWizPopUp.youCanSafelyCloseThisWindow');
    },
    helperAutoFireTimerComplete() {
      return this.backToHome
        ? this.$t('regWizPopUp.routes.success.countdown.error.complete')
        : this.$t('regWizPopUp.byeBye');
    },
    copyKeyUrlButtonText() {
      if (this.copyFeedback === false) return this.$t('regWizPopUp.routes.success.install.copyFail');
      if (this.copyFeedback) return this.$t('regWizPopUp.routes.success.install.copySuccess');
      return this.$t('regWizPopUp.routes.success.install.copyButton');
    },
    keyInstallError() {
      const licenseActionFail = this.licenseAction && !this.licenseAction.success;
      const licenseFromKeyServer = this.keyServerAction && this.license;
      if ((licenseFromKeyServer || this.timeout) && licenseActionFail) return true;
      return false;
    },
  },
  watch: {
    performingActions(val) {
      log.debug('[performingActions]: %o', val);
      /**
       * Once `performingActions` is false we will send a postMessage to the webGUI to ask for the latest state
       * then we determine what type of action to preform. Redirect to home view or close.
       */
      if (!val) {
        setTimeout(() => { // MASSIVE delay before asking for new server state
          this.updateServerState();
        }, 1000);
      }
    },
    /**
     * @description determine where to send the user when `performingActions` is done and we receive the pingback from the webGUI.
     * `receivedSuccessNewServerState` is set when pingback from postMessage event: 'SUCCESS_NEW_STATE' is received.
     * via mutation `RECEIVED_SUCCESS_NEW_SERVERSTATE`.
     * success is a timestamp. We also have new server state has been set in Vuex store. We'll use it to determine where to redirect the user.
     * False is error from webGUI on state receiving. There's isn't a new state in the Vuex store. Will eventually trigger the timeout error.
     * @param {Date} val
     */
    receivedSuccessNewServerState(val) {
      log.debug('[receivedSuccessNewServerState]: %o', val);
      if (val) {
        // errorMessage here was most likely captured by the 'DELIVER_NEW_STATE' stale state detection
        if (this.state === 'TRIAL' || (this.stateData.error && !this.errorMessage)) this.backToHome = true; // so we can provide the user context on what their error is for their server's state. But if it's an app errorMessage then we need them to close the pop-up…
        else this.closePopUp = true;
        this.loading = false;
      }
    },
    /**
     * Overwrite `errorMessage` if it includes the "owner" object response error. We don't care to show it on the success page.
     */
    errorMessage(val) {
      log.debug('[errorMessage]: %o', val);
      if (val && val.includes(ERROR_MESSAGE_UNEXPECTED_OWNER_RESPONSE)) this.SET_APP_ERROR_MESSAGE('');
    },
  },
  methods: {
    ...mapActions([
      'reloadWebGUI',
      'updateServerState',
    ]),
    ...mapMutations([
      // 'LICENSE_PINGBACK',
      // 'ACCOUNT_PINGBACK',
      // 'SET_APP_ERROR_MESSAGE',
      'SET_SHOW_POST_MESSAGE_STATUS',
    ]),
    helperAutoFireMethod() {
      return this.reloadWebGUI();
    },
    preventClose(e) {
      if (this.loading || (this.keyInstallError && !this.keyUrlCopied)) {
        e.preventDefault();
        // eslint-disable-next-line no-param-reassign
        e.returnValue = '';
        // eslint-disable-next-line no-alert
        alert(this.$t('regWizPopUp.closingPopUpMayLeadToErrors'));
      }
    },
    copyKeyUrl() {
      clearTimeout(this.copyTimeout);
      const resetBtn = () => {
        this.copyTimeout = setTimeout(() => {
          this.copyFeedback = null;
        }, 1500);
      };

      this.$copyText(this.license).then(() => {
        this.keyUrlCopied = true; // do not reset this value
        this.copyFeedback = true;
        resetBtn();
      }, () => {
        this.copyFeedback = true;
        resetBtn();
      });
    },
  },
  created() {
    // // explicit reset to prime ourselves for postMessage PINGBACKs from UPC
    // this.LICENSE_PINGBACK(null);
    // this.ACCOUNT_PINGBACK(null);
    // this.SET_APP_ERROR_MESSAGE(null);
    // // send latest data to UPC via postMessage
    // this.sendDataPostMessageToServer();
    window.addEventListener('beforeunload', this.preventClose);
  },
  mounted() {},
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.preventClose);
  },
};
</script>

<template>
  <ui-dropdown :show="showPostMessageStatus" @closeDropdown="SET_SHOW_POST_MESSAGE_STATUS(false)">
    <template v-if="loading">
      <UnraidLoading :height="64" class="mx-auto" />
      <h3 v-if="typeof loading === 'string'" class="text-20px italic mt-8px mb-0">{{ loading }}</h3>
    </template>
    <div v-else>
      <transition-group appear name="fade" mode="out-in">
        <template v-if="submissionFrom === 'SignIn' || submissionFrom === 'SignOut' || keyServerAction === 'recovery'">
          <SuccessTile
            v-if="submissionFrom === 'SignIn' || keyServerAction === 'recovery'"
            :loading="!accountAction"
            key="ServerAttached"
            :success="accountAction ? accountAction.success : true"
            :title="signInTileTitle" />
          <SuccessTile
            v-if="submissionFrom === 'SignOut'"
            :loading="!accountAction"
            key="ServerRemoved"
            :success="accountAction ? accountAction.success : true"
            :title="signOutTileTitle" />
        </template>
        <SuccessTile
          v-if="keyServerAction === 'extendTrial'"
          key="ExtendTrial"
          :success="true"
          :title="$t('regWizPopUp.routes.success.extended', [licenseType])" />
        <SuccessTile
          v-if="keyServerAction === 'recovery'"
          key="KeyRecovered"
          :success="true"
          :title="$t('regWizPopUp.routes.success.recovered', [licenseType])" />
        <SuccessTile
          v-if="keyServerAction === 'replace'"
          key="KeyReplaced"
          :success="true"
          :title="$t('regWizPopUp.routes.success.replaced', [licenseType])" />
        <SuccessTile
          v-if="keyServerAction === 'new'"
          key="KeyCreated"
          :success="true"
          :title="$t('regWizPopUp.routes.success.created', [licenseType])" />
        <!-- Key installs loading/success/error -->
        <template v-if="keyServerAction">
          <SuccessTile
            v-if="(!licenseAction || licenseAction.success) && CAN_POST_MESSAGE"
            :loading="!licenseAction"
            key="KeyInstalled"
            :success="true"
            :title="!licenseAction ? $t('regWizPopUp.routes.success.install.loading', [licenseType]) : $t('regWizPopUp.routes.success.install.success', [licenseType])" />
          <SuccessTile
            v-else
            key="KeyError"
            :success="false"
            :title="$t('regWizPopUp.routes.success.install.error', [licenseType])" />
        </template>
        <!-- send that naughty boy to timeout -->
        <SuccessTile
          v-if="timeout && !keyInstallError"
          key="SuccessTimeout"
          :success="false"
          :title="$t('regWizPopUp.routes.success.timeout', [servername])" />
        <!-- display manuall key install instructions on install error -->
        <div
          v-if="keyInstallError"
          key="manualKeyInstallInstructions"
          class="mt-4 text-red text-sm text-center">
          <p v-if="licenseAction && !licenseAction.success" class="text-left my-4"><em>{{ licenseAction.message }}</em></p>
          <p class="text-left my-4"><em>{{ $t('regWizPopUp.routes.success.install.manualInstructions') }}</em></p>
          <button @click="copyKeyUrl" class="button">{{ copyKeyUrlButtonText }}</button>
        </div>
        <p v-if="errorMessage" key="errorMessage" class="text-red text-left text-sm mt-4" v-html="errorMessage"></p>
      </transition-group>
    </div>
  </ui-dropdown>
</template>
